/**
 * Fixed templates for Misstatement types
 * These templates define the structure and fields for each misstatement type
 */

export type FieldType =
  | "number"
  | "text"
  | "dropdown"
  | "calendar"
  | "yes_no"
  | "multi_select_search"
  | "summary_table"
  | "calculated"
  | "static"
  | "table";

export interface TemplateField {
  id: string;
  templateType: "factual" | "judgmental" | "projected";
  label: string;
  type: FieldType;
  source?: string; // For dropdowns and multi-select
  expandable?: boolean; // For dropdowns
  options?: string[]; // For dropdowns with fixed options
  value?: string; // For static fields
  formula?: string; // For calculated fields
  valueFrom?: string; // For summary table rows
  visibleIf?: Record<string, string>; // Conditional visibility
  document?: boolean; // Whether field supports document upload
  documentIf?: "yes" | "no"; // When to show document upload
  rows?: Array<{ label: string; valueFrom: string }>; // For summary tables
  columns?: Array<{ id: string; label: string; type: string; source?: string; expandable?: boolean; options?: string[] }>; // For table fields
}

export interface MisstatementTemplate {
  type: "factual" | "judgmental" | "projected";
  fields: TemplateField[];
}

/**
 * Factual Misstatement Template
 */
export const factualTemplate: MisstatementTemplate = {
  type: "factual",
  fields: [
    {
      id: "factual_table",
      templateType: "factual",
      label: "Factual Misstatement Details",
      type: "table",
      columns: [
        { id: "factual_amount", label: "Amount", type: "number" },
        { id: "factual_description", label: "Description", type: "text" },
        { id: "factual_abcotd", label: "ABCOTD", type: "dropdown", source: "fs_line_items", expandable: true },
        { id: "factual_period", label: "Period", type: "calendar" },
        { id: "factual_type", label: "Type", type: "dropdown", options: ["Classification", "Disclosure", "Other"] },
      ],
    },
    {
      id: "factual_materiality_table",
      templateType: "factual",
      type: "summary_table",
      rows: [
        { label: "Misstatement", valueFrom: "factual_amount" },
        { label: "Materiality", valueFrom: "planning.materiality" },
        { label: "Performance Materiality", valueFrom: "planning.performanceMateriality" },
        { label: "Clearly Trivial Threshold", valueFrom: "planning.clearlyTrivialThreshold" },
      ],
    },
    {
      id: "factual_q_communicated",
      templateType: "factual",
      label: "Have we communicated the misstatement to management and those charged with governance?",
      type: "yes_no",
      document: true,
    },
    {
      id: "factual_q_profit_impact",
      templateType: "factual",
      label: "Does the misstatement have an impact on the entity's profit?",
      type: "yes_no",
      documentIf: "yes",
      document: true,
    },
    {
      id: "factual_q_offset",
      templateType: "factual",
      label: "Has the misstatement been offset by other misstatements?",
      type: "yes_no",
    },
    {
      id: "factual_q_offset_list",
      templateType: "factual",
      label: "Offsetting Misstatements",
      type: "multi_select_search",
      source: "all_misstatements",
      visibleIf: { factual_q_offset: "yes" },
    },
    {
      id: "factual_q_legal_violation",
      templateType: "factual",
      label: "Has the misstatement resulted in violations of legal or regulatory compliances?",
      type: "yes_no",
      documentIf: "yes",
      document: true,
    },
    {
      id: "factual_q_fraud",
      templateType: "factual",
      label: "Is the misstatement indicative of fraud?",
      type: "yes_no",
      documentIf: "yes",
      document: true,
    },
    {
      id: "factual_q_control_deficiency",
      templateType: "factual",
      label: "Is a control deficiency identified which has caused the misstatement?",
      type: "yes_no",
    },
    {
      id: "factual_q_deficiency_list",
      templateType: "factual",
      label: "Linked Control Deficiencies",
      type: "multi_select_search",
      source: "control_deficiencies",
      visibleIf: { factual_q_control_deficiency: "yes" },
    },
    {
      id: "factual_q_prior_period",
      templateType: "factual",
      label: "Is the misstatement caused due to a misstatement in prior periods?",
      type: "yes_no",
    },
    {
      id: "factual_q_prior_list",
      templateType: "factual",
      label: "Prior Period Misstatements",
      type: "multi_select_search",
      source: "all_misstatements",
      visibleIf: { factual_q_prior_period: "yes" },
    },
    {
      id: "factual_q_material_conclusion",
      templateType: "factual",
      label: "Is the misstatement individually or in aggregate causing the financial statements to be materially misstated?",
      type: "yes_no",
      documentIf: "yes",
      document: true,
    },
    {
      id: "factual_q_classification_specific",
      templateType: "factual",
      label: "Does the erroneous classification impact any material legal, regulatory or contractual requirement?",
      type: "yes_no",
      documentIf: "yes",
      document: true,
      visibleIf: { factual_type: "Classification" },
    },
    {
      id: "factual_q_disclosure_specific",
      templateType: "factual",
      label: "Does the wrongful disclosure significantly impact the user's interpretation of the financial statements?",
      type: "yes_no",
      documentIf: "yes",
      document: true,
      visibleIf: { factual_type: "Disclosure" },
    },
  ],
};

/**
 * Judgemental Misstatement Template
 */
export const judgmentalTemplate: MisstatementTemplate = {
  type: "judgmental",
  fields: [
    {
      id: "judgmental_table",
      templateType: "judgmental",
      label: "Judgemental Misstatement Details",
      type: "table",
      columns: [
        { id: "judgmental_auditor_value", label: "Value as per Auditor", type: "number" },
        { id: "judgmental_description", label: "Description", type: "text" },
        { id: "judgmental_management_estimate", label: "Management Estimate", type: "dropdown", source: "remb_management_estimates" },
        { id: "judgmental_abcotd", label: "ABCOTD", type: "dropdown", source: "fs_line_items", expandable: true },
        { id: "judgmental_period", label: "Period", type: "calendar" },
        { id: "judgmental_type", label: "Type", type: "dropdown", options: ["Classification", "Disclosure", "Other"] },
      ],
    },
    {
      id: "judgmental_materiality_table",
      templateType: "judgmental",
      type: "summary_table",
      rows: [
        { label: "Misstatement", valueFrom: "judgmental_misstatement_calculated" },
        { label: "Materiality", valueFrom: "planning.materiality" },
        { label: "Performance Materiality", valueFrom: "planning.performanceMateriality" },
        { label: "Clearly Trivial Threshold", valueFrom: "planning.clearlyTrivialThreshold" },
      ],
    },
    {
      id: "judgmental_q_communicated",
      templateType: "judgmental",
      label: "Have we communicated the misstatement to management and those charged with governance?",
      type: "yes_no",
      document: true,
    },
    {
      id: "judgmental_q_profit_impact",
      templateType: "judgmental",
      label: "Does the misstatement have an impact on the entity's profit?",
      type: "yes_no",
      documentIf: "yes",
      document: true,
    },
    {
      id: "judgmental_q_offset",
      templateType: "judgmental",
      label: "Has the misstatement been offset by other misstatements?",
      type: "yes_no",
    },
    {
      id: "judgmental_q_offset_list",
      templateType: "judgmental",
      label: "Offsetting Misstatements",
      type: "multi_select_search",
      source: "all_misstatements",
      visibleIf: { judgmental_q_offset: "yes" },
    },
    {
      id: "judgmental_q_legal_violation",
      templateType: "judgmental",
      label: "Has the misstatement resulted in violations of legal or regulatory compliances?",
      type: "yes_no",
      documentIf: "yes",
      document: true,
    },
    {
      id: "judgmental_q_fraud",
      templateType: "judgmental",
      label: "Is the misstatement indicative of fraud?",
      type: "yes_no",
      documentIf: "yes",
      document: true,
    },
    {
      id: "judgmental_q_control_deficiency",
      templateType: "judgmental",
      label: "Is a control deficiency identified which has caused the misstatement?",
      type: "yes_no",
    },
    {
      id: "judgmental_q_deficiency_list",
      templateType: "judgmental",
      label: "Linked Control Deficiencies",
      type: "multi_select_search",
      source: "control_deficiencies",
      visibleIf: { judgmental_q_control_deficiency: "yes" },
    },
    {
      id: "judgmental_q_prior_period",
      templateType: "judgmental",
      label: "Is the misstatement caused due to a misstatement in prior periods?",
      type: "yes_no",
    },
    {
      id: "judgmental_q_prior_list",
      templateType: "judgmental",
      label: "Prior Period Misstatements",
      type: "multi_select_search",
      source: "all_misstatements",
      visibleIf: { judgmental_q_prior_period: "yes" },
    },
    {
      id: "judgmental_q_material_conclusion",
      templateType: "judgmental",
      label: "Is the misstatement individually or in aggregate causing the financial statements to be materially misstated?",
      type: "yes_no",
      documentIf: "yes",
      document: true,
    },
    {
      id: "judgmental_q_classification_specific",
      templateType: "judgmental",
      label: "Does the erroneous classification impact any material legal, regulatory or contractual requirement?",
      type: "yes_no",
      documentIf: "yes",
      document: true,
      visibleIf: { judgmental_type: "Classification" },
    },
    {
      id: "judgmental_q_disclosure_specific",
      templateType: "judgmental",
      label: "Does the wrongful disclosure significantly impact the user's interpretation of the financial statements?",
      type: "yes_no",
      documentIf: "yes",
      document: true,
      visibleIf: { judgmental_type: "Disclosure" },
    },
  ],
};

/**
 * Projected Misstatement Template
 */
export const projectedTemplate: MisstatementTemplate = {
  type: "projected",
  fields: [
    {
      id: "projected_table",
      templateType: "projected",
      label: "Projected Misstatement Details",
      type: "table",
      columns: [
        { id: "projected_description", label: "Description", type: "text" },
        { id: "projected_abcotd", label: "ABCOTD", type: "dropdown", source: "fs_line_items", expandable: true },
        { id: "projected_period", label: "Period", type: "calendar" },
        { id: "projected_type", label: "Type", type: "dropdown", options: ["Classification", "Disclosure", "Other"] },
      ],
    },
    {
      id: "projected_calculation_table",
      templateType: "projected",
      label: "Projected Misstatement Calculation",
      type: "table",
      columns: [
        { id: "projected_particulars", label: "Particulars", type: "text" },
        { id: "projected_population", label: "Population", type: "number" },
        { id: "projected_misstatement", label: "Misstatement", type: "number" },
      ],
    },
    {
      id: "projected_materiality_table",
      templateType: "projected",
      type: "summary_table",
      rows: [
        { label: "Misstatement", valueFrom: "projected_misstatement_calculated" },
        { label: "Materiality", valueFrom: "planning.materiality" },
        { label: "Performance Materiality", valueFrom: "planning.performanceMateriality" },
        { label: "Clearly Trivial Threshold", valueFrom: "planning.clearlyTrivialThreshold" },
      ],
    },
    {
      id: "projected_q_communicated",
      templateType: "projected",
      label: "Have we communicated the misstatement to management and those charged with governance?",
      type: "yes_no",
      document: true,
    },
    {
      id: "projected_q_profit_impact",
      templateType: "projected",
      label: "Does the misstatement have an impact on the entity's profit?",
      type: "yes_no",
      documentIf: "yes",
      document: true,
    },
    {
      id: "projected_q_offset",
      templateType: "projected",
      label: "Has the misstatement been offset by other misstatements?",
      type: "yes_no",
    },
    {
      id: "projected_q_offset_list",
      templateType: "projected",
      label: "Offsetting Misstatements",
      type: "multi_select_search",
      source: "all_misstatements",
      visibleIf: { projected_q_offset: "yes" },
    },
    {
      id: "projected_q_legal_violation",
      templateType: "projected",
      label: "Has the misstatement resulted in violations of legal or regulatory compliances?",
      type: "yes_no",
      documentIf: "yes",
      document: true,
    },
    {
      id: "projected_q_fraud",
      templateType: "projected",
      label: "Is the misstatement indicative of fraud?",
      type: "yes_no",
      documentIf: "yes",
      document: true,
    },
    {
      id: "projected_q_control_deficiency",
      templateType: "projected",
      label: "Is a control deficiency identified which has caused the misstatement?",
      type: "yes_no",
    },
    {
      id: "projected_q_deficiency_list",
      templateType: "projected",
      label: "Linked Control Deficiencies",
      type: "multi_select_search",
      source: "control_deficiencies",
      visibleIf: { projected_q_control_deficiency: "yes" },
    },
    {
      id: "projected_q_prior_period",
      templateType: "projected",
      label: "Is the misstatement caused due to a misstatement in prior periods?",
      type: "yes_no",
    },
    {
      id: "projected_q_prior_list",
      templateType: "projected",
      label: "Prior Period Misstatements",
      type: "multi_select_search",
      source: "all_misstatements",
      visibleIf: { projected_q_prior_period: "yes" },
    },
    {
      id: "projected_q_material_conclusion",
      templateType: "projected",
      label: "Is the misstatement individually or in aggregate causing the financial statements to be materially misstated?",
      type: "yes_no",
      documentIf: "yes",
      document: true,
    },
    {
      id: "projected_q_classification_specific",
      templateType: "projected",
      label: "Does the erroneous classification impact any material legal, regulatory or contractual requirement?",
      type: "yes_no",
      documentIf: "yes",
      document: true,
      visibleIf: { projected_type: "Classification" },
    },
    {
      id: "projected_q_disclosure_specific",
      templateType: "projected",
      label: "Does the wrongful disclosure significantly impact the user's interpretation of the financial statements?",
      type: "yes_no",
      documentIf: "yes",
      document: true,
      visibleIf: { projected_type: "Disclosure" },
    },
  ],
};

/**
 * Get template by type
 */
export function getMisstatementTemplate(
  type: "Factual" | "Judgemental" | "Projected"
): MisstatementTemplate {
  switch (type) {
    case "Factual":
      return factualTemplate;
    case "Judgemental":
      return judgmentalTemplate;
    case "Projected":
      return projectedTemplate;
    default:
      return factualTemplate;
  }
}

/**
 * All templates
 */
export const misstatementTemplates = {
  factual: factualTemplate,
  judgmental: judgmentalTemplate,
  projected: projectedTemplate,
};

